import { useCallback, useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { Pencil2Icon } from "@radix-ui/react-icons";
import { TrashIcon } from "@radix-ui/react-icons";
import { Cross2Icon } from "@radix-ui/react-icons";
import { ShieldCheck } from "lucide-react";

import useReactForm from "../../hooks/useForm";
import validate from "src/helpers/validate";
import "../custom-styles.css";
import { TableWrapper } from "../Styles";
import { Button } from "./ui/button";
import { useToast } from "../common/ui/use-toast";
import { getUser, bulkTransfer, getBusinessWallet, walletNameCheck } from "src/services";
import { v4 as uuidv4 } from "uuid";
import { Input } from "./ui/input";
import { EmptyMessage } from "./Styles";
import { TransferDescription } from "../Styles";
import { FormItem, FormMessage } from "./ui/form";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  findWalletByNumber,
  getWalletLabel,
  getWalletNumber,
  normalizeWalletList,
  sanitizeWalletNumber,
} from "../../helpers/utils";
import {
  TransferBadge,
  TransferDialogBody,
  TransferDialogContent,
  TransferFieldGrid,
  TransferFooter,
  TransferFooterNote,
  TransferHeader,
  TransferSection,
  TransferSectionDescription,
  TransferSectionHeader,
  TransferSectionTitle,
} from "./TransferFormStyles";

const BulkSend = ({
  open,
  handleClose,
  businesses,
  isBusinessTransfer,
}: {
  open: boolean;
  handleClose: () => void;
  businesses?: any;
  isBusinessTransfer?: boolean;
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { values, errors, setValues, reset, handleChange } = useReactForm(validate);

  const [allTransfers, setAllTransfers] = useState<any>([]);
  const [confirmDetailsIsLoading] = useState<boolean>(false);
  const [openAddTransferItem, setOpenAddTransferItem] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);

  const { isLoading: walletIsLoading, data: userData } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
  });

  const { data: businessWalletData, isLoading: isLoadingBusinessWalletData } = useQuery({
    queryKey: ["businessWallet", selectedBusiness],
    queryFn: () => getBusinessWallet(selectedBusiness as string),
    enabled: !!selectedBusiness,
  });

  // Same split as InitiateTransfer: the backend already accepts a business
  // wallet as sender when the caller owns the business, so this is only a
  // matter of offering those wallets.
  const wallets = isBusinessTransfer
    ? normalizeWalletList(businessWalletData)
    : userData?.wallets;

  useEffect(() => {
    if (!isBusinessTransfer || selectedBusiness || !businesses?.length) {
      return;
    }

    const preferredBusiness =
      businesses.find((business: any) => business.is_default) ?? businesses[0];

    if (preferredBusiness?.id) {
      setSelectedBusiness(String(preferredBusiness.id));
    }
  }, [businesses, isBusinessTransfer, selectedBusiness]);

  function handleAddRow() {
    setOpenAddTransferItem(true);
  }

  const handleWalletSelect = (walletNumber: string) => {
    const selectedWallet = findWalletByNumber(wallets, walletNumber);

    setValues((prevValues: any) => ({
      ...prevValues,
      sender_wallet_number: walletNumber,
      currency: selectedWallet?.currency ?? selectedWallet?.currency_value ?? prevValues?.currency,
    }));
  };

  // Carried onto the row so the table shows who each line pays, and the payer
  // reads the whole run before submitting rather than one line at a time.
  const handlePayeeResolved = useCallback((name: string) => {
    setValues((prevValues: any) => ({ ...prevValues, name }));
  }, [setValues]);

  async function handleConfirmDetails() {
    // setConfirmDetailsIsLoading(true);

    const normalizedValues = {
      ...values,
      receiver_wallet_number: sanitizeWalletNumber(values?.receiver_wallet_number),
    };

    const filteredTransfers = allTransfers.filter((transfer: any) => transfer.id !== values?.id);

    isEdit
      ? setAllTransfers([normalizedValues, ...filteredTransfers])
      : setAllTransfers((prevTransfers: any) => {
          return [
            {
              ...normalizedValues,
              id: uuidv4(),
            },
            ...prevTransfers,
          ];
        });
    setOpenAddTransferItem(false);
    reset();
    setIsEdit(false);

    // try {
    //   const userIdData = await handleUserId(values?.receiver_wallet_number);
    //   if (userIdData?.user_id) {
    //     const receiverData = await handleGetParticularUser(userIdData.user_id);
    //     setConfirmDetailsIsLoading(false);
    //     setValues((prevValues: any) => ({
    //       ...prevValues,
    //       ...receiverData,
    //     }));

    //     toast({
    //       description: "Details were successfully confirmed.",
    //     });
    //   }
    // } catch (error: any) {
    //   setConfirmDetailsIsLoading(false);
    //   toast({
    //     variant: "destructive",
    //     title: "Error",
    //     description: error?.response?.data?.message ?? "Details are incorrect.",
    //   });
    // }
  }

  const bulkTransferMutation = useMutation({
    mutationKey: ["bulkTransfer"],
    mutationFn: (payload: any) => bulkTransfer(payload),
    onSuccess: async () => {
      toast({ description: "Your bulk transfer was successful" });
      queryClient.invalidateQueries({ queryKey: ["transferHistory"] });
      queryClient.refetchQueries({ queryKey: ["transferHistoryQueryKey"] });

      handleClose();
      setAllTransfers([]);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.response?.data?.message ?? "The bulk transfer encountered an error",
      });
    },
  });

  function handleBulkTransfer() {
    bulkTransferMutation.mutate(allTransfers);
  }

  function handleDelete(id: string) {
    const filteredTransfers = allTransfers.filter((transfer: any) => transfer?.id !== id);
    setAllTransfers(filteredTransfers);
  }

  function handleEdit(selectedValue: any) {
    setValues(selectedValue);
    setIsEdit(true);
    setOpenAddTransferItem(true);
  }

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" />
        <Dialog.Content className="DialogContentLarge">
          <Dialog.Title className="DialogTitle">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "1rem",
              }}
            >
              <div>
                Bulk Transfer
                <TransferDescription>
                  Send money in bulk to multiple recipients at once.
                </TransferDescription>
              </div>

              <div>
                <button
                  onClick={handleAddRow}
                  type="submit"
                  className="flex items-center my-1 mx-1 justify-center py-2 rounded-md bg-red-500 px-6 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                >
                  Add transfer
                </button>
              </div>
            </div>
          </Dialog.Title>

          <TableWrapper height="55vh">
            {Boolean(allTransfers.length) ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Amount</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Wallet number</TableHead>
                    <TableHead>Receiver's name</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allTransfers?.map((transfer: any) => (
                    <TableRow key={transfer?.id}>
                      <TableCell>{transfer?.amount}</TableCell>
                      <TableCell>{transfer?.currency}</TableCell>
                      <TableCell>{transfer?.receiver_wallet_number}</TableCell>
                      <TableHead>
                        {transfer?.name || (
                          <span className="text-xs text-amber-600">Unconfirmed</span>
                        )}
                      </TableHead>
                      <TableHead>
                        <div style={{ display: "flex" }}>
                          <TrashIcon
                            onClick={() => handleDelete(transfer?.id)}
                            style={{ marginRight: "0.5rem" }}
                            width={22}
                            height={22}
                          />
                          <Pencil2Icon
                            onClick={() => handleEdit(transfer)}
                            style={{ marginRight: "0.5rem" }}
                            width={20}
                            height={20}
                          />
                        </div>
                      </TableHead>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyMessage>
                <p>You have no transfers yet. Your added transfers will appear here.</p>
              </EmptyMessage>
            )}
          </TableWrapper>

          <div style={{ display: "flex", marginTop: 25, justifyContent: "flex-end" }}></div>
          <Dialog.Close asChild>
            <button className="IconButton" aria-label="Close" onClick={handleClose}>
              <Cross2Icon />
            </button>
          </Dialog.Close>

          <div style={{ display: "flex", marginTop: 25, justifyContent: "flex-end" }}>
            <Dialog.Close asChild>
              <button
                onClick={handleBulkTransfer}
                disabled={bulkTransferMutation.isPending}
                type="submit"
                className="flex items-center justify-center py-2 rounded-md bg-red-500 px-6 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
              >
                {bulkTransferMutation.isPending ? "Transfering..." : "Transfer"}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
      <AddItemModal
        open={openAddTransferItem}
        handleClose={() => {
          setOpenAddTransferItem(false);
          reset();
        }}
        values={values}
        handleChange={handleChange}
        errors={errors}
        handleWalletSelect={handleWalletSelect}
        wallets={wallets}
        walletIsLoading={walletIsLoading}
        isLoadingBusinessWalletData={isLoadingBusinessWalletData}
        isBusinessTransfer={isBusinessTransfer}
        selectedBusiness={selectedBusiness}
        onPayeeResolved={handlePayeeResolved}
        confirmDetailsIsLoading={confirmDetailsIsLoading}
        handleConfirmDetails={handleConfirmDetails}
      />
    </Dialog.Root>
  );
};

const AddItemModal = ({
  wallets,
  walletIsLoading,
  isLoadingBusinessWalletData,
  isBusinessTransfer,
  selectedBusiness,
  onPayeeResolved,
  values,
  handleChange,
  errors,
  handleWalletSelect,
  open,
  confirmDetailsIsLoading,
  handleConfirmDetails,
  handleClose,
}: any) => {
  const methods = useForm();

  const formatWalletNumber = (walletNumber: string | undefined | null): string => {
    if (!walletNumber) return "";
    return sanitizeWalletNumber(walletNumber)
      .slice(0, 16)
      .replace(/(\d{4})/g, "$1 ")
      .trim();
  };

  const [payeeState, setPayeeState] = useState<{
    status: "idle" | "loading" | "found" | "missing" | "unavailable";
    name?: string;
  }>({ status: "idle" });

  const receiverNumber = sanitizeWalletNumber(values?.receiver_wallet_number || "");

  useEffect(() => {
    if (receiverNumber.length !== 16) {
      setPayeeState({ status: "idle" });
      return;
    }

    let current = true;
    setPayeeState({ status: "loading" });

    walletNameCheck(receiverNumber)
      .then((data: any) => {
        if (!current) return;
        setPayeeState({ status: "found", name: data?.account_name });
        onPayeeResolved?.(data?.account_name || "");
      })
      .catch((error: any) => {
        if (!current) return;
        // A wallet that does not exist and a lookup that failed are different
        // problems: one means fix the number, the other means try again.
        setPayeeState({
          status: error?.response?.status === 404 ? "missing" : "unavailable",
        });
        onPayeeResolved?.("");
      });

    return () => {
      current = false;
    };
  }, [receiverNumber, onPayeeResolved]);

  const walletsLoading = walletIsLoading || isLoadingBusinessWalletData;
  const walletSelectDisabled =
    walletsLoading || (isBusinessTransfer && !selectedBusiness);
  const walletPlaceholder =
    isBusinessTransfer && !selectedBusiness
      ? "choose a business first"
      : walletsLoading
      ? "loading wallets..."
      : "select wallet";

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" />
        <TransferDialogContent className="DialogContent">
          <TransferDialogBody>
            <TransferHeader>
              <div className="flex-1">
                <TransferBadge>
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Transfer item
                </TransferBadge>
                <Dialog.Title className="DialogTitle mt-3">Add Transfer Item</Dialog.Title>
                <Dialog.Description className="DialogDescription">
                  Add the wallet, amount, and receiver for this bulk transfer entry.
                </Dialog.Description>
              </div>
            </TransferHeader>

            <FormProvider {...methods}>
              <div className="flex flex-col gap-4">
                <TransferSection>
                  <TransferSectionHeader>
                    <div>
                      <TransferSectionTitle>Funding wallet</TransferSectionTitle>
                      <TransferSectionDescription>
                        Choose the wallet that should fund this item.
                      </TransferSectionDescription>
                    </div>
                  </TransferSectionHeader>

                  <FormItem className="space-y-3">
                    <Label>Select wallet to transfer*</Label>

                    <Select
                      value={values?.sender_wallet_number || undefined}
                      disabled={walletSelectDisabled}
                      onValueChange={handleWalletSelect}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={walletPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {wallets?.map((wallet: any, index: number) => {
                            const walletNumber = getWalletNumber(wallet);

                            if (!walletNumber) {
                              return null;
                            }

                            return (
                              <SelectItem key={walletNumber} value={walletNumber}>
                                {getWalletLabel(wallet, index)}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormItem>
                </TransferSection>

                <TransferSection>
                  <TransferSectionHeader>
                    <div>
                      <TransferSectionTitle>Transfer details</TransferSectionTitle>
                      <TransferSectionDescription>
                        Add the amount and the receiver wallet number for this entry.
                      </TransferSectionDescription>
                    </div>
                  </TransferSectionHeader>

                  <TransferFieldGrid>
                    <FormItem className="space-y-3">
                      <Label>Amount to transfer*</Label>
                      <Input
                        name="amount"
                        placeholder="2000"
                        onChange={handleChange}
                        value={values?.amount || ""}
                        type="number"
                        min={1}
                      />
                      {errors?.amount && <FormMessage>{errors?.amountErrorMessage}</FormMessage>}
                    </FormItem>

                    <FormItem className="space-y-3">
                      <Label>Receiver&apos;s wallet number*</Label>
                      <Input
                        type="text"
                        name="receiver_wallet_number"
                        onChange={handleChange}
                        value={formatWalletNumber(values?.receiver_wallet_number || "")}
                        maxLength={19}
                      />
                      {errors?.receiver_wallet_number && (
                        <FormMessage>{errors?.receiverWalletNumberErrorMessage}</FormMessage>
                      )}
                      {/* Shown as soon as the number is complete, so a wrong
                          digit is caught here rather than after the money has
                          gone. */}
                      {payeeState.status === "loading" && (
                        <p className="text-xs text-slate-500">Checking account name...</p>
                      )}
                      {payeeState.status === "found" && (
                        <p className="text-xs font-semibold text-emerald-600">
                          {payeeState.name}
                        </p>
                      )}
                      {payeeState.status === "missing" && (
                        <p className="text-xs font-semibold text-rose-600">
                          No account found for that wallet number.
                        </p>
                      )}
                      {payeeState.status === "unavailable" && (
                        <p className="text-xs text-amber-600">
                          Could not confirm the account name. Check the number carefully
                          before sending.
                        </p>
                      )}
                    </FormItem>
                  </TransferFieldGrid>

                  <FormItem className="space-y-3">
                    <Label>Description</Label>
                    <Input
                      name="description"
                      placeholder="description"
                      onChange={handleChange}
                      value={values?.description || ""}
                    />
                  </FormItem>
                </TransferSection>
              </div>
            </FormProvider>

            <TransferFooter>
              <TransferFooterNote>
                You can keep editing items until you submit the full bulk transfer.
              </TransferFooterNote>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="rounded-2xl border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Cancel
                </Button>

                <Dialog.Close asChild>
                  <Button
                    type="button"
                    disabled={
                      !values?.sender_wallet_number ||
                      !values?.receiver_wallet_number ||
                      (values?.amount !== undefined &&
                        values?.amount !== null &&
                        parseFloat(values?.amount) < 0) ||
                      Boolean(Object.values(errors).length) ||
                      confirmDetailsIsLoading
                    }
                    onClick={handleConfirmDetails}
                    className="rounded-2xl bg-gradient-to-r from-rose-500 to-red-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-rose-500/20 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-rose-100"
                  >
                    {confirmDetailsIsLoading ? "Confirming details..." : "Confirm details"}
                  </Button>
                </Dialog.Close>
              </div>
            </TransferFooter>
          </TransferDialogBody>

          <Dialog.Close asChild>
            <button className="IconButton" aria-label="Close" onClick={handleClose}>
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </TransferDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default BulkSend;
